import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect } from "react";
import { getMyTrial } from "@/api/profileApi";
import { useSupabaseSession } from "@/hooks/useSupabaseSession";

const WaitingForApprovalPage: React.FC = () => {
	const { session, isLoading: isSessionLoading } = useSupabaseSession();

	const { data: trial, isLoading: isTrialLoading } = useQuery({
		queryKey: ["profile", "trial"],
		queryFn: ({ signal }) => getMyTrial({ signal }),
		enabled: !!session,
		retry: false,
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			if (status && status !== "PENDING") return false;

			const attempt = query.state.dataUpdateCount;
			if (attempt < 10) return 30_000;
			if (attempt < 30) return 60_000;
			return 120_000;
		},
		refetchIntervalInBackground: false,
	});

	useEffect(() => {
		if (isSessionLoading) return;
		if (session) return;

		const next = encodeURIComponent("/waiting");
		window.location.href = `/auth?next=${next}`;
	}, [isSessionLoading, session]);

	useEffect(() => {
		if (!session) return;
		if (isTrialLoading) return;
		if (trial?.status === "PENDING") return;

		window.location.href = "/app";
	}, [isTrialLoading, session, trial?.status]);

	if (isSessionLoading || !session || isTrialLoading) return null;

	return (
		<div className="min-h-screen surface-alt flex flex-col items-center justify-center p-4 font-sans">
			<div className="w-full max-w-md surface rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-subtle overflow-hidden relative">
				<div className="h-1.5 w-full bg-warning-500" />

				<div className="p-10 flex flex-col items-center">
					<div className="relative w-20 h-20 mb-8">
						<div className="absolute inset-0 bg-warning-400 opacity-20 rounded-full animate-ping" />
						<div className="relative w-full h-full bg-warning-50 rounded-full flex items-center justify-center text-warning-600">
							<svg
								aria-hidden="true"
								xmlns="http://www.w3.org/2000/svg"
								width="32"
								height="32"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2.5"
								strokeLinecap="round"
								strokeLinejoin="round"
							>
								<circle cx="12" cy="12" r="10" />
								<polyline points="12 6 12 12 16 14" />
							</svg>
						</div>
					</div>

					<h1 className="text-3xl font-bold text-primary mb-3 tracking-tight text-center">Account Pending</h1>

					<p className="text-center text-muted mb-10 leading-relaxed">
						We're currently reviewing your access request. This usually takes a few hours. You'll receive an email
						notification once your account is approved.
					</p>

					<a
						href="/logout"
						className="w-full py-3 px-4 surface border border-subtle rounded-xl text-muted font-medium soft-hover transition-all focus:ring-4 focus:ring-neutral-100 text-center block shadow-sm"
					>
						Sign Out
					</a>

					<p className="mt-8 text-xs text-subtle text-center">
						Need help?{" "}
						<a href="mailto:support@knowgem.com" className="underline hover-text transition-colors">
							Contact Support
						</a>
					</p>
				</div>
			</div>

			<div className="mt-8 text-subtle text-xs tracking-widest uppercase opacity-60">KnowGem</div>
		</div>
	);
};

export default WaitingForApprovalPage;
